import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiX, FiSearch } = FiIcons;

const KeyPhrasesInput = ({ keyPhrases = [], onChange, placeholder = "Enter key phrase..." }) => {
  const [currentPhrase, setCurrentPhrase] = useState('');

  const addKeyPhrase = () => {
    if (currentPhrase.trim() && !keyPhrases.includes(currentPhrase.trim())) {
      const newKeyPhrases = [...keyPhrases, currentPhrase.trim()];
      onChange(newKeyPhrases);
      setCurrentPhrase('');
    }
  };

  const removeKeyPhrase = (index) => {
    const newKeyPhrases = keyPhrases.filter((_, i) => i !== index);
    onChange(newKeyPhrases);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addKeyPhrase();
    }
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Input Field */}
      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
        <div className="flex-1 relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
          <input
            type="text"
            value={currentPhrase}
            onChange={(e) => setCurrentPhrase(e.target.value)}
            onKeyPress={handleKeyPress}
            className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
            placeholder={placeholder}
          />
        </div>
        <motion.button
          type="button"
          onClick={addKeyPhrase}
          disabled={!currentPhrase.trim()}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-red-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold hover:from-red-700 hover:to-red-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-lg text-sm sm:text-base"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 sm:w-5 sm:h-5" />
          <span>Add</span>
        </motion.button>
      </div>

      {/* Key Phrases List */}
      <AnimatePresence>
        {keyPhrases.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-2 sm:space-y-3"
          >
            <h4 className="text-xs sm:text-sm font-semibold text-gray-700 flex items-center space-x-2">
              <SafeIcon icon={FiSearch} className="w-3 h-3 sm:w-4 sm:h-4 text-purple-600" />
              <span>Added Key Phrases ({keyPhrases.length})</span>
            </h4>
            <div className="flex flex-wrap gap-2">
              {keyPhrases.map((phrase, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  whileHover={{ scale: 1.05 }}
                  className="bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-semibold shadow-md flex items-center space-x-1 sm:space-x-2 group"
                >
                  <span className="break-words">{phrase}</span>
                  <motion.button
                    type="button"
                    onClick={() => removeKeyPhrase(index)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="text-purple-600 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                  >
                    <SafeIcon icon={FiX} className="w-3 h-3 sm:w-4 sm:h-4" />
                  </motion.button>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Helper Text */}
      <p className="text-xs sm:text-sm text-gray-500 flex items-center space-x-1">
        <SafeIcon icon={FiSearch} className="w-3 h-3 sm:w-4 sm:h-4" />
        <span>Enter key phrases one by one and click "Add" or press Enter</span>
      </p>
    </div>
  );
};

export default KeyPhrasesInput;